/**
 * Layout 3 -- card drill-down: one card per node at the current level, a
 * breadcrumb to climb back, and a card's title to descend. Every card has its
 * own "Open" link to the node's screen and a button for the detail panel.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronRight, Info } from 'lucide-react';

import { ContextTag, HolderBox, RiskBar, RiskPill, StatusBadge } from './OverviewParts';
import { LEVEL_LABEL, detailPath, pluralise, type OrgNode, type OrgTree } from './overviewModel';
import type { ViewProps } from './viewProps';

function footFor(node: OrgNode): [string, string][] {
  const { counts } = node;
  switch (node.level) {
    case 'group':
      return [
        [String(counts.business_units), 'Units'],
        [String(counts.clients), 'Clients'],
        [String(counts.engagements), 'Engagements'],
      ];
    case 'unit':
      return [
        [String(counts.clients), 'Clients'],
        [String(counts.engagements), 'Engagements'],
        [String(counts.active_engagements), 'Active'],
      ];
    case 'client':
      return [
        [String(counts.engagements), 'Engagements'],
        [String(counts.active_engagements), 'Active'],
        [String(counts.high_risk), 'High risk'],
      ];
    case 'engagement':
      return [];
  }
}

function Card({
  node,
  tree,
  onDrill,
  onDetails,
}: {
  node: OrgNode;
  tree: OrgTree;
  onDrill: (id: string) => void;
  onDetails: (id: string) => void;
}) {
  const target = detailPath(node, tree.byId);
  const drillable = node.children.length > 0;
  return (
    <article className="ov-card">
      <div className="ov-card-top">
        <div>
          <span className="ov-level">{LEVEL_LABEL[node.level]}</span>
          <h4>
            <button
              type="button"
              className="ov-card-title"
              onClick={() => (drillable ? onDrill(node.id) : onDetails(node.id))}
            >
              {node.name}
              {drillable && <ChevronRight aria-hidden="true" />}
            </button>
          </h4>
          <div className="ov-card-tags">
            <StatusBadge node={node} />
            <ContextTag node={node} />
          </div>
        </div>
      </div>
      {node.inScope &&
        (node.level === 'engagement' ? (
          <div className="ov-rolepair">
            <HolderBox holder={node.head} kind="head" />
            <RiskPill riskLevel={node.riskLevel} />
          </div>
        ) : (
          <div className="ov-rolepair">
            <HolderBox holder={node.delivery} kind="delivery" />
            <HolderBox holder={node.sales} kind="sales" />
          </div>
        ))}
      {node.level !== 'engagement' && node.inScope && (
        <div className="ov-rsk">
          <span>
            Risk mix · {node.counts.high_risk} high, {node.counts.medium_risk} medium
          </span>
          <RiskBar node={node} />
        </div>
      )}
      <div className="ov-foot">
        {footFor(node).map(([value, label]) => (
          <div key={label}>
            <b>{value}</b>
            <span>{label}</span>
          </div>
        ))}
        <div className="ov-foot-actions">
          <button type="button" className="btn btn-secondary btn-icon btn-sm" aria-label={`Details for ${node.name}`} onClick={() => onDetails(node.id)}>
            <Info aria-hidden="true" />
          </button>
          {target && (
            <Link className="btn btn-secondary btn-icon btn-sm" to={target} aria-label={`Open ${node.name}`}>
              <ArrowUpRight aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

export function CardsView({ roots, tree, onSelect }: ViewProps) {
  const [stack, setStack] = useState<readonly string[]>([]);

  // Walk the picks down the FILTERED roots; a pick the search removed ends the walk.
  const trail: OrgNode[] = [];
  let level: readonly OrgNode[] = roots;
  for (const id of stack) {
    const found = level.find((candidate) => candidate.id === id);
    if (!found) break;
    trail.push(found);
    level = found.children;
  }
  const items = level;

  return (
    <div>
      <nav className="ov-crumbs" aria-label="Breadcrumb">
        {trail.length === 0 ? (
          <span className="cur" aria-current="page">All groups</span>
        ) : (
          <button type="button" onClick={() => setStack([])}>All groups</button>
        )}
        {trail.map((node, index) => (
          <span key={node.id} className="ov-crumb">
            <span className="sep" aria-hidden="true">/</span>
            {index === trail.length - 1 ? (
              <span className="cur" aria-current="page">{node.name}</span>
            ) : (
              <button type="button" onClick={() => setStack(stack.slice(0, index + 1))}>{node.name}</button>
            )}
          </span>
        ))}
      </nav>
      <p className="ov-card-count hint">
        {pluralise(items.length, 'card')}
      </p>
      <div className="ov-cgrid">
        {items.map((node) => (
          <Card
            key={node.id}
            node={node}
            tree={tree}
            onDrill={(id) => setStack([...trail.map((entry) => entry.id), id])}
            onDetails={onSelect}
          />
        ))}
      </div>
    </div>
  );
}
